/**
 * Document Requests Hook
 * ======================
 * Hooks for creating and managing document requests.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

/**
 * Create a document request for an applicant.
 * Returns an object with mutateAsync, isPending, error, etc.
 */
export function useCreateDocumentRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ applicantId, documentTypes, reason, instructions, expiresInDays }) => {
      const response = await api.post(`/applicants/${applicantId}/document-requests`, {
        document_types: documentTypes,
        reason,
        instructions,
        expires_in_days: expiresInDays || 7,
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate document requests list for this applicant
      queryClient.invalidateQueries({ queryKey: ['document-requests', variables.applicantId] });
    },
  });
}

/**
 * Get all document requests for an applicant.
 */
export function useDocumentRequests(applicantId) {
  return useQuery({
    queryKey: ['document-requests', applicantId],
    queryFn: async () => {
      const response = await api.get(`/applicants/${applicantId}/document-requests`);
      return response.data;
    },
    enabled: !!applicantId,
  });
}

/**
 * Get a document request by token (public, no auth).
 */
export function usePublicDocumentRequest(token) {
  return useQuery({
    queryKey: ['public-document-request', token],
    queryFn: async () => {
      // Use a different base URL or axios instance that doesn't include auth headers
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://getclearance-production.up.railway.app'}/api/v1/upload/${token}`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to load document request');
      }
      return response.json();
    },
    enabled: !!token,
    retry: false, // Don't retry on 404/410
  });
}

/**
 * Upload a document for a document request (public, no auth).
 */
export function usePublicUpload() {
  return useMutation({
    mutationFn: async ({ token, file, documentType }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://getclearance-production.up.railway.app'}/api/v1/upload/${token}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      return response.json();
    },
  });
}
